import tinycolor from "tinycolor2";
import AdaptiveUiTheme from "@avinet/adaptive-ui-core/adaptive-ui/AdaptiveUiTheme";
import { avbase } from "@avinet/adaptive-ui-core/adaptive-ui/themes/AVBase";
import { icons } from "./icons";

// TODO Move to adaptive-ui-core when settled (?)

const colors = {
  "gray-25": "#fcfcfd",
  "gray-50": "#f8fafc",
  "gray-100": "#f2f4f7",
  "gray-200": "#e3e8ef",
  "gray-300": "#CDD5DF",
  "gray-400": "#98a2b3",
  "gray-500": "#697586",
  "gray-600": "#4B5565",
  "gray-700": "#344054",
  "gray-800": "#1d2939",
  "gray-900": "#121926",
  "error-25": "#fffbfa",
  "error-50": "#fef3f2",
  "error-100": "#fee4e2",
  "error-200": "#fecdca",
  "error-300": "#fda29b",
  "error-400": "#f97066",
  "error-500": "#f04438",
  "error-600": "#d92d20",
  "error-700": "#b42318",
  "error-800": "#912018",
  "error-900": "#7a271a",
  "warning-25": "#fffcf5",
  "warning-50": "#fffaeb",
  "warning-100": "#fef0c7",
  "warning-200": "#fedf89",
  "warning-300": "#fec84b",
  "warning-400": "#fdb022",
  "warning-500": "#f79009",
  "warning-600": "#dc6803",
  "warning-700": "#b54708",
  "warning-800": "#93370d",
  "warning-900": "#7a2e0e",
  "success-25": "#f6fef9",
  "success-50": "#ecfdf3",
  "success-100": "#d1fadf",
  "success-200": "#a6f4c5",
  "success-300": "#6ce9a6",
  "success-400": "#32d583",
  "success-500": "#12b76a",
  "success-600": "#039855",
  "success-700": "#027a48",
  "success-800": "#05603a",
  "success-900": "#054f31",
  "blue-gray-25": "#fcfcfd",
  "blue-gray-50": "#f8f9fc",
  "blue-gray-100": "#eaecf5",
  "blue-gray-200": "#d5d9eb",
  "blue-gray-300": "#afb5d9",
  "blue-gray-400": "#717bbc",
  "blue-gray-500": "#4e5ba6",
  "blue-gray-600": "#3e4784",
  "blue-gray-700": "#363f72",
  "blue-gray-800": "#293056",
  "blue-gray-900": "#101323",
  "blue-light-25": "#f5fbff",
  "blue-light-50": "#f0f9ff",
  "blue-light-100": "#e0f2fe",
  "blue-light-200": "#b9e6fe",
  "blue-light-300": "#7cd4fd",
  "blue-light-400": "#36bffa",
  "blue-light-500": "#0ba5ec",
  "blue-light-600": "#0086c9",
  "blue-light-700": "#026aa2",
  "blue-light-800": "#065986",
  "blue-light-900": "#0b4a6f",
  "blue-25": "#f5faff",
  "blue-50": "#eff8ff",
  "blue-100": "#d1e9ff",
  "blue-200": "#b2ddff",
  "blue-300": "#84caff",
  "blue-400": "#53b1fd",
  "blue-500": "#2e90fa",
  "blue-600": "#1570ef",
  "blue-700": "#175cd3",
  "blue-800": "#1849a9",
  "blue-900": "#194185",
  "indigo-25": "#f5f8ff",
  "indigo-50": "#eef4ff",
  "indigo-100": "#e0eaff",
  "indigo-200": "#c7d7fe",
  "indigo-300": "#a4bcfd",
  "indigo-400": "#8098f9",
  "indigo-500": "#6172f3",
  "indigo-600": "#444ce7",
  "indigo-700": "#3538cd",
  "indigo-800": "#2d31a6",
  "indigo-900": "#2d3282",
  "purple-25": "#fafaff",
  "purple-50": "#f4f3ff",
  "purple-100": "#ebe9fe",
  "purple-200": "#d9d6fe",
  "purple-300": "#bdb4fe",
  "purple-400": "#9b8afb",
  "purple-500": "#7a5af8",
  "purple-600": "#6938ef",
  "purple-700": "#5925dc",
  "purple-800": "#4a1fb8",
  "purple-900": "#3e1c96",
  "pink-25": "#fef6fb",
  "pink-50": "#fdf2fa",
  "pink-100": "#fce7f6",
  "pink-200": "#fcceee",
  "pink-300": "#faa7e0",
  "pink-400": "#f670c7",
  "pink-500": "#ee46bc",
  "pink-600": "#dd2590",
  "pink-700": "#c11574",
  "pink-800": "#9e165f",
  "pink-900": "#851651",
  "rose-25": "#fff5f6",
  "rose-50": "#fff1f3",
  "rose-100": "#ffe4e8",
  "rose-200": "#fecdd6",
  "rose-300": "#fea3b4",
  "rose-400": "#fd6f8e",
  "rose-500": "#f63d68",
  "rose-600": "#e31b54",
  "rose-700": "#c01048",
  "rose-800": "#a11043",
  "rose-900": "#89123e",
  "orange-25": "#fffaf5",
  "orange-50": "#fff6ed",
  "orange-100": "#ffead5",
  "orange-200": "#fddcab",
  "orange-300": "#feb273",
  "orange-400": "#fd853a",
  "orange-500": "#fb6514",
  "orange-600": "#ec4a0a",
  "orange-700": "#c4320a",
  "orange-800": "#9c2a10",
  "orange-900": "#7e2410",
} as Record<string, string>;

function shadesOf(name: string, invert = false) {
  if (invert) {
    const lighter = tinycolor(colors[`${name}-900`]);
    return {
      lighter: lighter
        .darken((lighter.getBrightness() / 2.55) * 0.5)
        .toHexString(),
      light: colors[`${name}-800`],
      tint: colors[`${name}-700`],
      default: colors[`${name}-600`],
      shade: colors[`${name}-400`],
      dark: colors[`${name}-300`],
      darker: colors[`${name}-100`],
    };
  }
  return {
    lighter: colors[`${name}-50`],
    light: colors[`${name}-100`],
    tint: colors[`${name}-500`],
    default: colors[`${name}-600`],
    shade: colors[`${name}-700`],
    dark: colors[`${name}-800`],
    darker: colors[`${name}-900`],
  };
}

export const gaia: AdaptiveUiTheme = {
  ...avbase,
  fontStack:
    'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
  headingFontStack: undefined,
  icons,
  name: "gaia",
  headingFontWeight: 600,
  defaultTextColor: colors["gray-700"],
  headingTextColor: colors["gray-900"],
  labelTextColor: colors["gray-700"],
  labelFontWeight: 600,
  fontWeight: 400,
  headingFontSizes: ["28px", "20px", "18px", "16px", "14px", "14px"],
  buttonPadding: "8px 16px",
  buttonRadius: 8,
  smallButtonPadding: "4px 6px",
  smallButtonRadius: 4,
  largeButtonPadding: "12px 20px",
  largeButtonRadius: 8,
  defaultSpacing: 8,
  radius: 4,
  inputPadding: "8px 10px",
  inputBorderColor: colors["gray-300"],
  inputBorderColorSingle: colors["gray-300"],
  inputBorderWidth: 1,
  inputBackground: "#fff",
  primary: shadesOf("indigo"),
  secondary: shadesOf("blue"),
  tertiary: shadesOf("purple"),
  tertiaryContrast: "#fff",
  success: shadesOf("success"),
  warning: shadesOf("warning"),
  danger: shadesOf("error"),
  successContrast: "#fff",
  warningContrast: "#fff",
  dangerContrast: "#fff",
  dark: "hsl(218, 28%, 17%)",
  medium: "hsl(215, 27%, 91%)",
  mediumContrast: "#222428",
  light: {
    lighter: "#ffffff",
    light: colors["gray-25"],
    tint: colors["gray-50"],
    default: colors["gray-200"],
    shade: colors["gray-300"],
    dark: colors["gray-500"],
    darker: colors["gray-600"],
  },
  lightContrast: colors["gray-600"],
  linkTextColor: colors["indigo-600"],
};

export const gaiaDark: AdaptiveUiTheme = {
  ...gaia,
  name: "gaia-dark",
  invert: true,
  defaultTextColor: "#fff",
  labelTextColor: undefined,
  primary: shadesOf("indigo", true),
  secondary: shadesOf("blue", true),
  tertiary: shadesOf("blue-light", true),
  success: shadesOf("success", true),
  warning: shadesOf("warning", true),
  danger: shadesOf("error", true),
  dark: "#f6f7f8",
  darkContrast: "#2f3237",
  medium: "hsl(210, 40%, 20%)",
  mediumContrast: "#f4f5f8",
  light: "hsl(218, 28%, 7%)",
  lightContrast: "hsl(200, 20%, 90%)",
  linkTextColor: colors["indigo-300"],
  headingTextColor: "#fff",
  inputBorderColor: "hsl(210, 30%, 20%)",
  inputBackground: undefined,
  inputTextColor: undefined,
};
